/**
 * PersistentRoute.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Keeps a page component mounted in the DOM regardless of which page the user
 * navigates to, toggling visibility with CSS only.  Component state (in-progress
 * extractions, form data, logs, scroll position) is never lost.
 *
 * WHY THIS EXISTS
 * ───────────────
 * VatWorkflow and BankStatementWorkflow hold all their working state in local
 * useState hooks.  When the parent renders conditionally:
 *
 *     {page === 'vat' && <VatWorkflow />}
 *
 * ...switching pages unmounts VatWorkflow, destroying everything: the active
 * extraction, the purchased results, the progress bar, the log trail.
 *
 * PersistentRoute fixes this with one rule: once a route has been shown, it
 * stays rendered.  Only its visibility changes.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * USAGE — in ClientDashboard (or wherever page routing happens)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   import PersistentRoute from './PersistentRoute';
 *
 *   // Replace:
 *   {page === 'vat'  && <VatWorkflow  clientId={id} clientName={name} />}
 *   {page === 'bank' && <BankStatementWorkflow bankSub={bankSub} />}
 *
 *   // With:
 *   <PersistentRoute active={page === 'vat'}>
 *     <VatWorkflow clientId={id} clientName={name} />
 *   </PersistentRoute>
 *
 *   <PersistentRoute active={page === 'bank'}>
 *     <BankStatementWorkflow bankSub={bankSub} />
 *   </PersistentRoute>
 *
 *   // Non-workflow pages that are cheap to remount can stay as-is:
 *   {page === 'clients' && <ClientsPage />}
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW IT WORKS
 * ─────────────────────────────────────────────────────────────────────────────
 * A ref latches to `true` the first time `active` is truthy.  After that, the
 * children are always rendered — but wrapped in a div whose `display` is either
 * 'contents' (visible) or 'none' (hidden).  React never sees an unmount, so
 * useState, useEffect intervals, and in-flight fetch promises all continue
 * uninterrupted.
 *
 * Before a route is first visited, nothing is rendered at all, so you pay zero
 * render cost for pages the user never opens.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useRef } from 'react';

/**
 * @param {object}  props
 * @param {boolean} props.active    - true = visible, false = hidden
 * @param {React.ReactNode} props.children
 * @param {object}  [props.style]   - extra inline styles on the wrapper div
 */
export default function PersistentRoute({ active, children, style = {} }) {
  // Latch: once the route is shown for the first time, always keep it rendered.
  const hasBeenActive = useRef(false);
  if (active) hasBeenActive.current = true;

  // Never visited yet — don't pay the render cost.
  if (!hasBeenActive.current) return null;

  return (
    <div
      style={{
        display: active ? 'contents' : 'none',
        ...style,
      }}
      // Screen readers skip hidden routes.
      aria-hidden={active ? undefined : true}
    >
      {children}
    </div>
  );
}
